import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { ManagedAgentImport } from '../managed-agents/types';

/**
 * Import settings of the workspace's imported agents, by PromptSafe agent ID.
 * Reloads whenever `agentsVersion` changes, e.g. after an import, or on
 * `reload()`.
 */
export function useManagedAgentImports(
  workspaceId: string | undefined,
  agentsVersion: unknown,
) {
  const [imports, setImports] = useState<Record<string, ManagedAgentImport>>(
    {},
  );
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    supabase
      .from('managed_agent_imports')
      .select(
        'agent_id, anthropic_agent_id, anthropic_agent_version, environment_id, tool_policy, budget_cents, imported_at, last_verified_at',
      )
      .eq('workspace_id', workspaceId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Loading imported agents failed:', error);
          return;
        }
        setImports(
          Object.fromEntries((data ?? []).map((row) => [row.agent_id, row])),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, agentsVersion, reloads]);

  return { imports, reload };
}
